import moment from "moment";

interface LeaveRequest {
  state: string;
  leaveType: string;
  startDate: Date | string;
  endDate: Date | string;
}

interface OverlappingRequest {
  type: string;
  startDate: Date;
  endDate: Date;
}

interface ValidationResult {
  hasOverlap: boolean;
  overlappingRequests: OverlappingRequest[];
}

/**
 * Validates if repeated Home Office requests overlap with existing leave requests.
 *
 * @param selectedWeekDays - Array of selected weekday numbers (0-6, where 0 is Sunday)
 * @param repeatedStartDate - Start date of the repeated HO period
 * @param repeatedEndDate - End date of the repeated HO period
 * @param leaveRequests - Existing leave requests to check against
 * @returns Validation result with overlap status and list of overlapping requests
 */
export function validateRepeatedHOCoverage(
  selectedWeekDays: number[],
  repeatedStartDate: Date,
  repeatedEndDate: Date,
  leaveRequests: LeaveRequest[]
): ValidationResult {
  let hasOverlap = false;
  const overlappingRequests: OverlappingRequest[] = [];
  const current = moment(repeatedStartDate).clone();
  const end = moment(repeatedEndDate);

  // Check each selected weekday in the date range
  while (current.isSameOrBefore(end, 'day')) {
    const dayOfWeek = current.day();

    if (selectedWeekDays.includes(dayOfWeek)) {
      const currentDate = current.toDate();

      // Find overlapping requests for this specific date
      const overlapping = leaveRequests.filter((item) => {
        if (item.state === "rejected" || item.state === "cancelled") return false;

        const itemStart = moment(item.startDate).startOf("day");
        const itemEnd = moment(item.endDate).endOf("day");
        const checkDate = moment(currentDate).startOf("day");

        return checkDate.isBetween(itemStart, itemEnd, undefined, "[]");
      });

      if (overlapping.length > 0) {
        hasOverlap = true;
        overlapping.forEach((req) => {
          // Only add if not already in the list
          const alreadyAdded = overlappingRequests.some(
            (existing) =>
              existing.type === req.leaveType &&
              moment(existing.startDate).isSame(req.startDate, 'day') &&
              moment(existing.endDate).isSame(req.endDate, 'day')
          );
          if (!alreadyAdded) {
            overlappingRequests.push({
              type: req.leaveType,
              startDate: new Date(req.startDate),
              endDate: new Date(req.endDate),
            });
          }
        });
      }
    }

    current.add(1, 'day');
  }

  return {
    hasOverlap,
    overlappingRequests,
  };
}
